import { isValidKey, type KeyFilter, type ObjectToEntries } from "../lib/utils";
import type { ParameterOptions, ParameterRecord, ParamType } from "./params.types";

/**
 *
 */
export const paramTypes = ["search", "hash"] as const satisfies readonly ParamType[];

/**
 *
 *
 */
export const typeDelimiterMap = {
  search: "?",
  hash: "#",
} as const satisfies Record<ParamType, string>;

export type ParamsCache = Map<string, string[]>;

/**
 *
 *
 *
 *
 *
 */
export const getOptions = (
  base: ParameterOptions,
  call: ParameterOptions | undefined,
): ParameterOptions => {
  return { ...base, ...call };
};

/**
 *
 *
 *
 *
 *
 */
export const fromStringParams = (rawParams: string, filter: KeyFilter[] = []): ParamsCache => {
  let sanitizedString = rawParams;

  /*                                                                                    */
  if (rawParams.startsWith("?") || rawParams.startsWith("#")) {
    sanitizedString = rawParams.slice(1);
  }

  /*                                                                   */
  const res: ParamsCache = new Map();
  for (const param of sanitizedString.split("&")) {
    const [key, ...value] = param.split("=");

    if (!key) continue;
    if (!isValidKey(key, filter)) continue;
    if (!res.has(key)) res.set(key, []);

    res.get(key)!.push(value.join("="));
  }

  return res;
};

/**
 *
 *
 *
 *
 */
export const toStringParams = (cache: ParamsCache, filter: KeyFilter[] = []): string => {
  /*                                                                                    */
  const parts: string[] = [];
  for (const [key, values] of cache) {
    if (!isValidKey(key, filter)) continue;
    for (const value of values) {
      parts.push(`${key}=${value}`);
    }
  }

  /*                                                                    */
  return parts.join("&");
};

/**
 *
 *
 *
 *
 */
export const toEntries = <P extends ParameterRecord>(
  cache: ParamsCache,
  options: ParameterOptions,
): ObjectToEntries<P> => {
  const res: [string, string][] = [];

  for (const [key, values] of cache) {
    for (const value of values) {
      res.push([key, options.decode ? decodeURI(value) : value]);
    }
  }

  return res as never;
};

/**
 *
 *
 *
 *
 *
 */
export const coerceValue = <R>(value: string | null): R => {
  /*                                             */
  if (value === null) return null as never;

  /*                                                          */
  if (value === "undefined") return undefined as never;

  /*                                                                   */
  try {
    return JSON.parse(value) as never;
  } catch {
    return value as never;
  }
};

export const uncoerceValue = (value: unknown): string => {
  return typeof value === "string" ? value : JSON.stringify(value);
};

export const appendEntries = <P extends ParameterRecord>(
  cache: ParamsCache,
  entries: ObjectToEntries<P>,
  onlyMissing = false,
): ParamsCache => {
  for (const [key, value] of entries) {
    if (!cache.has(key as string)) cache.set(key as string, []);
    const values = cache.get(key as string)!;

    if (onlyMissing && cache.has(key as string)) continue;

    if (Array.isArray(value)) value.forEach((v) => values.push(uncoerceValue(v)));
    else values.push(uncoerceValue(value));
  }

  return cache;
};

export const deleteValue = (cache: ParamsCache, key: string, value?: string): boolean => {
  /*                                     */
  if (value === undefined) return cache.delete(key);

  /*                                                             */
  if (!cache.has(key)) return false;
  const values = cache.get(key)!;

  /*                                                                     */
  if (!values.includes(value!)) return false;

  /*                                        */
  values.splice(values.indexOf(value!), 1);

  /*                                          */
  /*                                                 */
  if (values.length === 0) cache.delete(key);

  return true;
};

type ObjectReducer<R> = (
  acc: Record<PropertyKey, R>,
  [key, value]: [PropertyKey, R],
) => Record<PropertyKey, R>;

/**
 *
 *
 *
 *
 *
 *
 */
export const getObjectReducer: <R>(coercer?: (_: string) => R) => ObjectReducer<R> = (
  coercer = (_) => _ as never,
) => {
  return (acc, [key, value]) => {
    const coerced = coercer(value as string);

    /*                                                      */
    if (!(key in acc)) {
      acc[key] = coerced;
    }
    /*                                                             */
    else if (Array.isArray(acc[key])) {
      acc[key].push(coerced);
    }
    /*                                                                            */
    else {
      acc[key] = [acc[key], coerced] as never;
    }

    return acc;
  };
};

export const mergeParamCache = (
  target: ParamsCache,
  source: ParamsCache,
  filters: KeyFilter[],
  deleted: Set<string>,
): ParamsCache => {
  /*                                                                                                */
  for (const key of source.keys()) {
    /*                                                                                       */
    if (!isValidKey(key, filters)) continue;
    /*                                                                 */
    target.set(key, source.get(key)!);
  }

  /*                                                           */
  for (const key of target.keys()) {
    if (filters.length < 1 || !isValidKey(key, filters)) continue;
    if (!source.has(key)) target.delete(key);
  }

  /*                                                */
  for (const deletedKey of deleted) {
    if (!source.has(deletedKey)) target.delete(deletedKey);
  }

  return target;
};
